import { Link } from "react-router-dom";
import Nav from "@/components/Nav";
import Footer from "@/components/Footer";
import { Yantra, OrnamentDivider, MandalaMark, OmGlyph } from "@/components/VedicOrnaments";
import HoverTooltip from "@/components/HoverTooltip";
import { GrahaCard, NakshatraCard } from "@/components/JyotishaTooltips";
import { useReveal } from "@/lib/useReveal";
import { ArrowRight, ArrowUpRight } from "lucide-react";

// Navagraha — the nine classical lights of Vedic astrology
const NAVAGRAHA = [
  { sa: "सूर्य", en: "Surya",   eng: "Sun",      glyph: "☉", role: "The Soul · Self · Vitality" },
  { sa: "चन्द्र", en: "Chandra", eng: "Moon",     glyph: "☽", role: "The Mind · Feeling · Memory" },
  { sa: "मङ्गल", en: "Mangala", eng: "Mars",     glyph: "♂", role: "Courage · Action · Fire" },
  { sa: "बुध",   en: "Budha",   eng: "Mercury",  glyph: "☿", role: "Intellect · Speech · Wit" },
  { sa: "गुरु",  en: "Guru",    eng: "Jupiter",  glyph: "♃", role: "Wisdom · Dharma · Expansion" },
  { sa: "शुक्र", en: "Shukra",  eng: "Venus",    glyph: "♀", role: "Love · Beauty · Harmony" },
  { sa: "शनि",  en: "Shani",   eng: "Saturn",   glyph: "♄", role: "Discipline · Time · Karma" },
  { sa: "राहु",  en: "Rahu",    eng: "N. Node",  glyph: "☊", role: "Desire · The Unfamiliar" },
  { sa: "केतु",  en: "Ketu",    eng: "S. Node",  glyph: "☋", role: "Release · The Past" },
];

// 27 Nakshatras — lunar mansions
const NAKSHATRAS = [
  { sa: "अश्विनी", en: "Ashwini",    ruler: "Ketu",   deity: "Ashvins" },
  { sa: "भरणी",   en: "Bharani",    ruler: "Venus",  deity: "Yama" },
  { sa: "कृत्तिका", en: "Krittika",   ruler: "Sun",    deity: "Agni" },
  { sa: "रोहिणी",  en: "Rohini",     ruler: "Moon",   deity: "Brahma" },
  { sa: "मृगशिरा", en: "Mrigashira", ruler: "Mars",   deity: "Soma" },
  { sa: "आर्द्रा",  en: "Ardra",      ruler: "Rahu",   deity: "Rudra" },
  { sa: "पुनर्वसु", en: "Punarvasu",  ruler: "Jupiter",deity: "Aditi" },
  { sa: "पुष्य",   en: "Pushya",     ruler: "Saturn", deity: "Brihaspati" },
  { sa: "आश्लेषा", en: "Ashlesha",   ruler: "Mercury",deity: "Nagas" },
  { sa: "मघा",    en: "Magha",      ruler: "Ketu",   deity: "Pitris" },
  { sa: "पू. फाल्गुनी", en: "P. Phalguni", ruler: "Venus", deity: "Bhaga" },
  { sa: "उ. फाल्गुनी", en: "U. Phalguni", ruler: "Sun",   deity: "Aryaman" },
  { sa: "हस्त",   en: "Hasta",      ruler: "Moon",   deity: "Savitar" },
  { sa: "चित्रा",  en: "Chitra",     ruler: "Mars",   deity: "Vishvakarma" },
  { sa: "स्वाती",  en: "Swati",      ruler: "Rahu",   deity: "Vayu" },
  { sa: "विशाखा", en: "Vishakha",   ruler: "Jupiter",deity: "Indra-Agni" },
  { sa: "अनुराधा", en: "Anuradha",  ruler: "Saturn", deity: "Mitra" },
  { sa: "ज्येष्ठा", en: "Jyeshtha",   ruler: "Mercury",deity: "Indra" },
  { sa: "मूल",    en: "Mula",       ruler: "Ketu",   deity: "Nirriti" },
  { sa: "पू. आषाढ़ा", en: "P. Ashadha",ruler: "Venus",  deity: "Apas" },
  { sa: "उ. आषाढ़ा", en: "U. Ashadha",ruler: "Sun",    deity: "Vishvadevas" },
  { sa: "श्रवण",   en: "Shravana",   ruler: "Moon",   deity: "Vishnu" },
  { sa: "धनिष्ठा", en: "Dhanishta",  ruler: "Mars",   deity: "Vasus" },
  { sa: "शतभिषा", en: "Shatabhisha", ruler: "Rahu",  deity: "Varuna" },
  { sa: "पू. भाद्रपद", en: "P. Bhadra", ruler: "Jupiter", deity: "Aja Ekapada" },
  { sa: "उ. भाद्रपद", en: "U. Bhadra", ruler: "Saturn", deity: "Ahir Budhnya" },
  { sa: "रेवती",   en: "Revati",     ruler: "Mercury",deity: "Pushan" },
];

const Landing = () => {
  const ref = useReveal();

  return (
    <div className="min-h-screen" ref={ref}>
      <Nav />

      {/* =========================== HERO =========================== */}
      <section className="relative grain overflow-hidden">
        <div className="max-w-[1500px] mx-auto px-6 md:px-14 pt-10 md:pt-16 pb-20 md:pb-28">
          {/* Top marginalia */}
          <div className="flex items-center justify-between mb-12 md:mb-20 text-xs font-mono text-ink-muted uppercase tracking-[0.32em]">
            <span className="flex items-center gap-3">
              <OmGlyph size={16} /> भृगु · Est. 2026
            </span>
            <span className="hidden md:block">Vol. I · Jyotisha · The Knowledge of Light</span>
            <span className="hidden md:block">{new Date().toLocaleDateString("en-US", { month: "long", day: "numeric", year: "numeric" })}</span>
          </div>

          <div className="grid md:grid-cols-12 gap-8 md:gap-12 items-start">
            <div className="md:col-span-7">
              <div className="eyebrow mb-10" data-testid="hero-label">
                Ch. 00 — A Vedic Reading
              </div>

              <h1 className="font-display display-xl text-ink mb-10" data-testid="hero-title">
                The sky,<br />
                <span className="font-display-italic text-terracotta">deciphered</span><br />
                in the <span className="font-display-italic text-terracotta">ancient</span> tongue.
              </h1>

              <div className="max-w-xl border-l-2 border-terracotta pl-6 mb-12">
                <p className="pull-quote text-2xl md:text-3xl text-ink leading-tight mb-5" data-testid="hero-sub">
                  Jyotisha — <span className="devanagari-accent">ज्योतिष</span> — is the science of light.
                  For five thousand years, it has read the heavens the moment a soul entered the world.
                </p>
                <p className="text-ink-soft text-base md:text-lg leading-relaxed">
                  Bhrigu computes your birth chart — the <em className="font-display-italic">kundali</em> — to the arc-second, then answers your questions in the old way: planet, house, nakshatra, by name. No generic horoscopes. No mystical fog.
                </p>
              </div>

              <div className="flex flex-wrap items-center gap-4 mb-3">
                <Link to="/signup" className="btn-primary" data-testid="hero-cta-signup">
                  Cast my kundali <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
              <div className="mb-14 text-sm font-mono tracking-wide text-terracotta" data-testid="hero-multilingual">
                🌐 Available in Hindi, Tamil, Telugu + 11 more
              </div>

              <div className="grid grid-cols-3 gap-6 pt-10 border-t border-[var(--hairline)]" data-testid="hero-meta">
                {[
                  { n: "27", l: "Nakshatras" },
                  { n: "09", l: "Navagraha" },
                  { n: "12", l: "Bhavas · Houses" },
                ].map((s, i) => (
                  <div key={i}>
                    <div className="font-display display-md text-terracotta">{s.n}</div>
                    <div className="text-xs uppercase tracking-widest text-ink-muted mt-1">{s.l}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Yantra */}
            <div className="md:col-span-5 hidden md:block">
              <div className="relative">
                <div className="animate-drift" data-testid="hero-yantra">
                  <Yantra size={540} className="w-full h-auto" />
                </div>
                <div className="absolute -top-4 -left-4 text-xs font-mono text-ink-muted uppercase tracking-widest">
                  Fig. 01 · Sri Yantra
                </div>
                <div className="absolute -bottom-6 right-0 text-xs font-mono text-ink-muted uppercase tracking-widest">
                  The geometry of becoming
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom band - scrolling ancients */}
        <div className="border-y border-[var(--hairline)] bg-cream-soft overflow-hidden">
          <div className="flex items-center py-4 gap-12 animate-slow-rotate-reverse font-display-italic text-lg text-ink-soft whitespace-nowrap" style={{ animation: "none" }}>
            {["ज्योतिष · Jyotisha", "ग्रह · Graha", "राशि · Rashi", "नक्षत्र · Nakshatra", "भाव · Bhava", "दशा · Dasha", "योग · Yoga", "पञ्चाङ्ग · Panchanga"].map((w, i) => (
              <span key={i} className="flex items-center gap-12">
                <MandalaMark size={20} color="#C9962B" />
                {w}
              </span>
            ))}
            {["ज्योतिष · Jyotisha", "ग्रह · Graha", "राशि · Rashi", "नक्षत्र · Nakshatra", "भाव · Bhava", "दशा · Dasha", "योग · Yoga", "पञ्चाङ्ग · Panchanga"].map((w, i) => (
              <span key={`b-${i}`} className="flex items-center gap-12">
                <MandalaMark size={20} color="#C9962B" />
                {w}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* =========================== CHAPTER 01 · THE TRADITION =========================== */}
      <section className="py-28 md:py-40">
        <div className="max-w-[1500px] mx-auto px-6 md:px-14">
          <OrnamentDivider className="mb-20 reveal" />

          <div className="grid md:grid-cols-12 gap-10 md:gap-16">
            <div className="md:col-span-4 reveal">
              <div className="num-stamp mb-3">01</div>
              <div className="section-label">The Living Tradition</div>
            </div>
            <div className="md:col-span-8">
              <h2 className="font-display display-xl text-ink mb-14 reveal">
                Astrology, as it was meant to be <span className="font-display-italic text-terracotta">read.</span>
              </h2>
              <div className="grid md:grid-cols-2 gap-10 md:gap-16 max-w-4xl">
                <p className="text-ink text-lg leading-relaxed reveal reveal-delay-1">
                  In the Vedic tradition, a birth chart is not a fortune. It is a map — <span className="devanagari-accent">कुण्डली</span> — drawn the moment you arrived, read in the language of light, ruled by nine <em className="font-display-italic">grahas</em> moving through twelve <em className="font-display-italic">bhavas</em>, anchored to twenty-seven <em className="font-display-italic">nakshatras</em>.
                </p>
                <p className="text-ink-soft text-lg leading-relaxed reveal reveal-delay-2">
                  For two centuries the West has told us our Sun sign. But the sages of Bharata have always known — the Moon's nakshatra matters more, Jupiter's dasha rules your decade, and your lagna reveals who the world sees when you arrive. Bhrigu computes it all, with precision, in your hand.
                </p>
              </div>

              <div className="mt-20 pl-6 border-l-2 border-terracotta max-w-3xl reveal">
                <p className="pull-quote text-3xl md:text-4xl text-terracotta mb-4">
                  "यथा पिण्डे तथा ब्रह्माण्डे"
                </p>
                <p className="pull-quote text-xl md:text-2xl text-ink mb-3">
                  As in the atom, so in the universe.
                </p>
                <p className="text-xs font-mono text-ink-muted uppercase tracking-widest">
                  — Foundational principle of Jyotisha
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================== CHAPTER 02 · NAVAGRAHA =========================== */}
      <section className="py-28 md:py-40 bg-cream-deep relative overflow-hidden">
        <div className="max-w-[1500px] mx-auto px-6 md:px-14">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-20 reveal">
            <div>
              <div className="num-stamp mb-3">02</div>
              <div className="section-label mb-6">The Navagraha · नवग्रह</div>
              <h2 className="font-display display-lg text-ink max-w-3xl">
                Nine lights. Nine <span className="font-display-italic text-terracotta">forces.</span>
              </h2>
            </div>
            <p className="text-ink-soft max-w-sm text-lg leading-relaxed">
              Each planet — each <em className="font-display-italic">graha</em> — governs a frequency of your being. In Jyotisha, they are not impersonal stones. They are <em className="font-display-italic">shaktis</em>, living forces you meet every day.
            </p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3 gap-px bg-[var(--hairline-gold)] border border-[var(--hairline-gold)]" data-testid="navagraha-grid">
            {NAVAGRAHA.map((g, i) => (
              <HoverTooltip
                key={g.en}
                content={<GrahaCard graha={g} />}
                className="block h-full"
              >
                <div className="planet-card reveal cursor-help relative h-full" style={{ animationDelay: `${i * 0.05}s` }} data-testid={`nav-graha-${g.en}`}>
                  <div className="flex items-start justify-between mb-6">
                    <span className="planet-symbol">{g.glyph}</span>
                    <span className="font-mono text-[10px] uppercase tracking-widest text-ink-muted">0{i + 1}</span>
                  </div>
                  <div className="font-sanskrit text-3xl text-terracotta mb-1 leading-none">{g.sa}</div>
                  <div className="font-display text-2xl text-ink mb-2 leading-none" style={{ fontWeight: 500 }}>
                    {g.en}<span className="text-ink-muted text-base font-body font-normal ml-2">· {g.eng}</span>
                  </div>
                  <div className="text-xs text-ink-soft leading-relaxed mt-3 border-t border-[var(--hairline)] pt-3">
                    {g.role}
                  </div>
                  <div className="absolute right-3 bottom-3 text-[8px] font-mono uppercase tracking-[0.25em] text-terracotta opacity-0 group-hover:opacity-70 transition-opacity">
                    hover for lore
                  </div>
                </div>
              </HoverTooltip>
            ))}
          </div>
        </div>
      </section>

      {/* =========================== CHAPTER 03 · NAKSHATRAS =========================== */}
      <section className="py-28 md:py-40">
        <div className="max-w-[1500px] mx-auto px-6 md:px-14">
          <div className="grid md:grid-cols-12 gap-10 md:gap-16 mb-16">
            <div className="md:col-span-4 reveal">
              <div className="num-stamp mb-3">03</div>
              <div className="section-label mb-6">The Twenty-Seven · नक्षत्राणि</div>
            </div>
            <div className="md:col-span-8">
              <h2 className="font-display display-lg text-ink mb-8 reveal">
                The Moon's <span className="font-display-italic text-terracotta">mansions.</span>
              </h2>
              <p className="text-ink-soft text-lg leading-relaxed max-w-2xl reveal reveal-delay-1">
                Long before the twelve zodiac signs, the Vedic astronomers mapped the night in twenty-seven finer slices — each the width of one day's journey of the Moon. Your nakshatra is the constellation the Moon stood within, the hour you arrived. It is the truest note of your nature.
              </p>
            </div>
          </div>

          <div className="overflow-x-auto ribbon-scroll pb-6 -mx-6 md:-mx-14 px-6 md:px-14 reveal">
            <div className="flex min-w-max nakshatra-ribbon border border-[var(--hairline-gold)]">
              {NAKSHATRAS.map((n, i) => (
                <HoverTooltip
                  key={n.en}
                  content={<NakshatraCard nakshatra={n} index={i} />}
                  placement="auto"
                >
                  <div className="nakshatra-card" data-testid={`nakshatra-${i}`}>
                    <div className="text-xs font-mono text-terracotta mb-2">{String(i + 1).padStart(2, "0")}</div>
                    <div className="font-sanskrit text-2xl text-ink leading-none mb-1">{n.sa}</div>
                    <div className="font-display text-base text-ink mb-3" style={{ fontWeight: 500 }}>{n.en}</div>
                    <div className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">
                      {n.ruler} · {n.deity}
                    </div>
                  </div>
                </HoverTooltip>
              ))}
            </div>
          </div>
          <p className="text-xs text-ink-muted font-mono text-center mt-6 tracking-widest uppercase">
            ← scroll · hover any nakshatra for the classical teaching →
          </p>
        </div>
      </section>

      {/* =========================== CHAPTER 04 · THE PROCESS =========================== */}
      <section className="py-28 md:py-40 bg-ink text-cream-soft relative overflow-hidden">
        <div className="absolute inset-0 grain opacity-50" />
        <div className="max-w-[1500px] mx-auto px-6 md:px-14 relative">
          <div className="mb-20 reveal">
            <div className="num-stamp mb-3" style={{ color: "var(--gold-soft)" }}>04</div>
            <div className="section-label mb-6" style={{ color: "var(--gold-soft)" }}>The Process</div>
            <h2 className="font-display display-lg max-w-4xl">
              Four steps. One <span className="font-display-italic" style={{ color: "var(--gold-soft)" }}>reading.</span>
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-px bg-[var(--hairline-gold)]">
            {[
              { n: "I", sa: "जन्म", en: "Birth", body: "Give us your date, time, and place — the three coordinates of your arrival." },
              { n: "II", sa: "गणना", en: "Computation", body: "Real ephemeris math, Lahiri ayanamsha, whole-sign houses. Every planet, every degree." },
              { n: "III", sa: "प्रश्न", en: "Question", body: "Ask in plain language. About love, work, dharma, the year ahead — anything." },
              { n: "IV", sa: "उत्तर", en: "Answer", body: "Vakya, reading your actual chart, answers in seconds. Cited. Specific. Grounded." },
            ].map((s, i) => (
              <div key={s.n} className="bg-ink p-10 reveal" style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="font-display-italic text-5xl mb-6" style={{ color: "var(--gold-soft)" }}>{s.n}</div>
                <div className="font-sanskrit text-3xl mb-2" style={{ color: "var(--terracotta)" }}>{s.sa}</div>
                <div className="font-display text-2xl mb-4" style={{ fontWeight: 500 }}>{s.en}</div>
                <p className="text-sm leading-relaxed opacity-70">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* =========================== CHAPTER 05 · CHART SHOWCASE =========================== */}
      <section className="py-28 md:py-40">
        <div className="max-w-[1500px] mx-auto px-6 md:px-14">
          <OrnamentDivider className="mb-20" />

          <div className="grid md:grid-cols-12 gap-12 items-center">
            <div className="md:col-span-6 reveal order-2 md:order-1">
              <div className="num-stamp mb-3">05</div>
              <div className="section-label mb-6">The Chart · कुण्डली</div>
              <h2 className="font-display display-lg text-ink mb-10">
                Drawn as the rishis <span className="font-display-italic text-terracotta">drew it.</span>
              </h2>
              <p className="text-ink text-lg leading-relaxed mb-8 max-w-lg">
                The North Indian kundali — diamond-split square, Ascendant at the crown. Nine planets. Twelve bhavas. Twenty-seven nakshatras woven through. The oldest visual language of light, computed new for you.
              </p>

              <div className="grid grid-cols-2 gap-6 mb-10 max-w-md">
                {[
                  { l: "Ayanamsha", v: "Lahiri" },
                  { l: "Houses", v: "Whole-sign" },
                  { l: "Precision", v: "Arc-second" },
                  { l: "Engine", v: "Swiss Ephem." },
                ].map((s, i) => (
                  <div key={i} className="border-t border-terracotta pt-3">
                    <div className="text-[10px] uppercase tracking-widest text-ink-muted font-mono">{s.l}</div>
                    <div className="font-display text-xl text-ink" style={{ fontWeight: 500 }}>{s.v}</div>
                  </div>
                ))}
              </div>

              <Link to="/signup" className="btn-primary">
                See my kundali <ArrowUpRight className="w-4 h-4" />
              </Link>
            </div>

            <div className="md:col-span-6 reveal reveal-delay-1 order-1 md:order-2">
              <div className="bg-cream-soft border border-[var(--border)] p-8 md:p-10 relative">
                <div className="absolute top-4 right-4 text-[10px] font-mono text-ink-muted uppercase tracking-widest">
                  Fig. 02 · Kundali Chart
                </div>
                <svg viewBox="0 0 400 400" className="w-full h-auto" data-testid="landing-sample-chart">
                  <rect x="2" y="2" width="396" height="396" fill="#FBEFD4" stroke="#C9962B" strokeWidth="1.2" />
                  <line x1="2" y1="2" x2="398" y2="398" stroke="#C9962B" strokeWidth="1.2" />
                  <line x1="398" y1="2" x2="2" y2="398" stroke="#C9962B" strokeWidth="1.2" />
                  <polygon points="200,2 398,200 200,398 2,200" fill="none" stroke="#C9962B" strokeWidth="1.2" />
                  {[
                    [200, 100, "Asc · Vi"], [100, 50, "Li"], [50, 100, "Sc"], [100, 200, "Sa"],
                    [50, 300, "Cp"], [100, 350, "Aq"], [200, 300, "Pi"], [300, 350, "Ar"],
                    [350, 300, "Ta"], [300, 200, "Ge"], [350, 100, "Cn"], [300, 50, "Le"],
                  ].map(([x, y, l], i) => (
                    <text key={i} x={x} y={y - 28} textAnchor="middle" className="vedic-chart-sign" style={{ fontSize: 11 }}>{l}</text>
                  ))}
                  {[
                    [200, 100, "Su"], [100, 200, "Mo"], [300, 200, "Ma · Ju"], [200, 300, "Sa"],
                    [50, 300, "Ve"], [350, 100, "Me"],
                  ].map(([x, y, p], i) => (
                    <text key={`p-${i}`} x={x} y={y + 5} textAnchor="middle" className="vedic-chart-planet" style={{ fontSize: 14 }}>
                      {p}
                    </text>
                  ))}
                </svg>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* =========================== CHAPTER 06 · VOICES =========================== */}
      <section className="py-28 md:py-40 bg-cream-deep">
        <div className="max-w-[1500px] mx-auto px-6 md:px-14">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-20 reveal">
            <div>
              <div className="num-stamp mb-3">06</div>
              <div className="section-label mb-6">Voices</div>
              <h2 className="font-display display-lg text-ink max-w-3xl">
                Not predictions. <span className="font-display-italic text-terracotta">Reflections.</span>
              </h2>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-px bg-[var(--hairline)]">
            {[
              { q: "I've consulted three jyotishis in Chennai. This reads my chart like they do — but faster, and it doesn't forget what I asked ten questions ago.", by: "Priya R.", city: "Chennai" },
              { q: "I asked about my Saturn return. It told me the exact nakshatra, the exact degree, and a question I'd been avoiding for years.", by: "Marcus L.", city: "Toronto" },
              { q: "Western apps tell me I'm a Taurus. My Vedic moon is Krittika. This was the first app that knew the difference — and why it matters.", by: "Aisha K.", city: "Dubai" },
            ].map((t, i) => (
              <figure key={i} className="bg-cream-deep p-10 md:p-12 reveal" style={{ animationDelay: `${i * 0.1}s` }}>
                <div className="text-6xl font-display-italic text-terracotta leading-none mb-6">"</div>
                <blockquote className="font-display text-2xl leading-snug text-ink mb-8" style={{ fontWeight: 400 }}>
                  {t.q}
                </blockquote>
                <figcaption className="pt-6 border-t border-[var(--hairline)]">
                  <div className="font-display text-lg text-ink" style={{ fontWeight: 500 }}>{t.by}</div>
                  <div className="text-xs font-mono text-ink-muted uppercase tracking-widest mt-1">{t.city}</div>
                </figcaption>
              </figure>
            ))}
          </div>
        </div>
      </section>

      {/* =========================== CTA =========================== */}
      <section className="py-32 md:py-44 bg-terracotta text-cream-soft relative overflow-hidden">
        <div className="absolute inset-0 flex items-center justify-center opacity-10 pointer-events-none">
          <Yantra size={800} />
        </div>
        <div className="max-w-[1400px] mx-auto px-6 md:px-14 text-center relative">
          <OmGlyph size={72} className="mb-8 block" />
          <h2 className="font-display display-xl leading-[0.85] mb-12">
            Begin your<br />
            <span className="font-display-italic">reading.</span>
          </h2>
          <p className="pull-quote text-xl md:text-2xl max-w-2xl mx-auto mb-14 opacity-90">
            Your first reading, on us. A real kundali. Answered in the ancient tongue.
          </p>
          <Link
            to="/signup"
            className="inline-flex items-center gap-4 bg-cream-soft text-terracotta px-12 py-6 uppercase tracking-[0.3em] text-xs hover:bg-cream-deep transition-colors font-medium"
            data-testid="cta-bottom"
          >
            Cast my kundali <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Landing;
